import { useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { deleteNews, getNews, patchNews, postNews } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

import { useEditor, EditorContent, useEditorState } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'

const MainContainer = styled.div`
  max-width: 1100px;
  margin: 10px auto;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;
  padding: 10px;

  @media (max-width: 1024px) {
    max-width: 95%;
    padding: 15px;
  }

  @media (max-width: 768px) {
    padding: 12px;
    margin: 5px auto;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 8px;
    margin: 0 auto;
  }
`;

const Title = styled.h2`
  color: #2c3e50;
  text-align: center;
  margin-bottom: 15px;
  font-weight: 600;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 8px;
  font-size: 18px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 20px;
    padding-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 15px;
    padding-bottom: 8px;
  }
`;

const FormContainer = styled.form`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  padding: 15px;
  margin-bottom: 20px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 20px;
    margin-bottom: 25px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 15px;
    margin-bottom: 20px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
  border-bottom: 2px solid #e0e0e0;
`;

const TabButton = styled.button`
  padding: 8px 16px;
  border: none;
  background: ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#666'};
  cursor: pointer;
  font-weight: ${props => props.active ? '600' : '400'};
  border-radius: 4px 4px 0 0;
  transition: all 0.2s;
  font-size: 13px;

  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : '#f5f5f5'};
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    flex: 1;
    padding: 8px 10px;
    font-size: 12px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 12px;

  @media (max-width: 768px) {
    margin-bottom: 18px;
  }

  @media (max-width: 480px) {
    margin-bottom: 15px;
  }

  label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #34495e;
    font-size: 12px;

    @media (max-width: 768px) {
      font-size: 14px;
      margin-bottom: 8px;
      font-weight: 600;
    }

    @media (max-width: 480px) {
      font-size: 13px;
      margin-bottom: 6px;
    }
  }

  input,
  textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 12px;
    transition: all 0.3s;
    box-sizing: border-box;

    @media (max-width: 768px) {
      padding: 12px;
      font-size: 16px;
      border-radius: 6px;
    }

    @media (max-width: 480px) {
      padding: 10px;
      font-size: 16px;
      border-radius: 5px;
    }

    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
    }
  }

  textarea {
    min-height: 80px;
    resize: vertical;

    @media (max-width: 768px) {
      min-height: 100px;
    }

    @media (max-width: 480px) {
      min-height: 90px;
    }
  }

  input[type="file"] {
    padding: 6px;
    font-size: 12px;

    @media (max-width: 768px) {
      padding: 8px;
      font-size: 14px;
    }
  }
`;

const ErrorMessage = styled.span`
  color: #e74c3c;
  font-size: 11px;
  margin-top: 4px;
  display: block;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 12px;

  @media (max-width: 768px) {
    gap: 10px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    margin-top: 15px;
  }
`;

const Button = styled.button`
  background-color: ${(props) =>
    props.red
      ? "#e74c3c"
      : props.secondary
        ? "#7f8c8d"
        : "var(--primary-color)"};
  color: white;
  padding: ${(props) => (props.small ? "6px 10px" : "8px 14px")};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: ${(props) => (props.small ? "10px" : "11px")};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  flex: ${(props) => (props.small ? "1" : "none")};
  min-width: 0;
  touch-action: manipulation;

  @media (max-width: 768px) {
    padding: ${(props) => (props.small ? "10px 12px" : "12px 20px")};
    font-size: ${(props) => (props.small ? "13px" : "14px")};
    border-radius: 6px;
    min-height: 44px;
  }

  @media (max-width: 480px) {
    padding: ${(props) => (props.small ? "8px 10px" : "10px 16px")};
    font-size: ${(props) => (props.small ? "12px" : "13px")};
    min-height: 40px;
    flex: 1;
  }

  &:hover {
    background-color: ${(props) =>
    props.red
      ? "#c0392b"
      : props.secondary
        ? "#6c7a7a"
        : "var(--primary-color)"};
    transform: translateY(-1px);
    filter: brightness(0.9);

    @media (max-width: 480px) {
      transform: none;
    }
  }

  &:active {
    transform: translateY(0);
    filter: brightness(0.8);
  }

  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
    transform: none;
  }
`;

const NewsSection = styled.div`
  margin-top: 20px;

  @media (max-width: 768px) {
    margin-top: 25px;
  }

  @media (max-width: 480px) {
    margin-top: 20px;
  }
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eaeaea;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 18px;
    padding-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 15px;
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  max-width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const NewsCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 15px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
  border-left: 2px solid var(--primary-color);
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);

    @media (max-width: 480px) {
      transform: none;
    }
  }

  @media (max-width: 768px) {
    padding: 18px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const NewsHeader = styled.div`
  margin-bottom: 10px;
  min-width: 0;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const NewsTitle = styled.h4`
  margin: 0 0 6px 0;
  color: #2c3e50;
  font-size: 13px;
  font-weight: 600;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const NewsDate = styled.p`
  margin: 0;
  color: #7f8c8d;
  font-size: 11px;
  word-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const NewsImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    height: 180px;
  }
`;

const Description = styled.p`
  margin: 0 0 12px 0;
  color: #555;
  line-height: 1.5;
  font-size: 11px;
  flex-grow: 1;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-height: 54px;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 15px;
    -webkit-line-clamp: 4;
    max-height: 90px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 12px;
    -webkit-line-clamp: 3;
    max-height: 60px;
  }
`;

const ActionButtons = styled.div`
display: flex;
gap: 6px;
margin-top: auto;
padding-top: 8px;
border-top: 1px solid #f0f0f0;

@media(max-width: 768px) {
  gap: 10px;
  padding-top: 12px;
}

@media(max-width: 480px) {
  flex-direction: column;
  gap: 8px;
}
`;

const PaginationContainer = styled.div`
display: flex;
justify-content: center;
align-items: center;
gap: 6px;
margin-top: 20px;
flex-wrap: wrap;

@media(max-width: 768px) {
  gap: 8px;
  margin-top: 25px;
}

@media(max-width: 480px) {
  gap: 5px;
  margin-top: 20px;
}
`;

const PageButton = styled.button`
padding: 6px 10px;
border: 1px solid ${(props) => (props.active ? "var(--primary-color)" : "#ddd")};
background-color: ${(props) => (props.active ? "var(--primary-color)" : "#fff")};
color: ${(props) => (props.active ? "#fff" : "#333")};
cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
border-radius: 4px;
font-size: 11px;
transition: all 0.2s;
min-width: 32px;
opacity: ${(props) => (props.disabled ? "0.5" : "1")};
touch-action: manipulation;

  &:hover:not(:disabled) {
  background-color: ${(props) =>
    props.active ? "var(--primary-color)" : "#f5f5f5"
  };
  border-color: var(--primary-color);

  @media(max-width: 480px) {
    background-color: ${(props) =>
    props.active ? "var(--primary-color)" : "#fff"
  };
  }
}

@media(max-width: 768px) {
  padding: 10px 14px;
  font-size: 14px;
  min-width: 40px;
  min-height: 40px;
}

@media(max-width: 480px) {
  padding: 8px 10px;
  font-size: 13px;
  min-width: 36px;
  min-height: 36px;
}
`;

const EmptyState = styled.div`
text-align: center;
padding: 30px;
color: #95a5a6;
background: #f9f9f9;
border-radius: 6px;
font-size: 12px;

@media(max-width: 768px) {
  padding: 40px 20px;
  font-size: 15px;
  border-radius: 8px;
}

@media(max-width: 480px) {
  padding: 30px 15px;
  font-size: 14px;
}
`;

const MobileScrollHint = styled.div`
text-align: center;
padding: 8px;
background: #e8f4fd;
border-radius: 4px;
font-size: 11px;
color: #2980b9;
margin-bottom: 12px;

@media(max-width: 768px) {
  font-size: 13px;
  padding: 10px;
}

@media(max-width: 480px) {
  font-size: 12px;
  padding: 8px;
}
`;

const LanguageSwitcher = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
  justify-content: center;
`;

const LangButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : '#ddd'};
  background: ${props => props.active ? 'var(--primary-color)' : '#fff'};
  color: ${props => props.active ? '#fff' : '#666'};
  cursor: pointer;
  border-radius: 4px;
  font-size: 11px;
  transition: all 0.2s;

  &:hover {
    border-color: var(--primary-color);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
  }
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  margin-top: 10px;
`;

const PreviewWrapper = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid ${props => props.isExisting ? '#3498db' : '#e0e0e0'};
  opacity: ${props => props.toRemove ? '0.4' : '1'};
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #f9f9f9;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(231, 76, 60, 0.8);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  padding: 0;

  &:hover {
    background: #e74c3c;
  }
`;

const RestoreButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 10px;
`;

const EditorToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

const ToolbarButton = styled.button`
  border: 1px solid ${props => props.$active ? 'var(--primary-color)' : '#d9d9d9'};
  background: ${props => props.$active ? 'var(--primary-color)' : '#fff'};
  color: ${props => props.$active ? '#fff' : '#333'};
  border-radius: 4px;
  min-width: 34px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;

  &[data-variant='bold'] {
    font-weight: 700;
  }

  &[data-variant='italic'] {
    font-style: italic;
  }

  &[data-variant='underline'] {
    text-decoration: underline;
  }

  &[data-role='action'] {
    min-width: 52px;
    font-size: 11px;
    letter-spacing: 0.2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditorWrapper = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;

  .tiptap {
    min-height: 160px;
    padding: 10px;
    font-size: 13px;
    line-height: 1.55;
    outline: none;
  }

  .tiptap p {
    margin: 0 0 8px;
  }

  .tiptap ul,
  .tiptap ol {
    margin: 0 0 8px 20px;
  }

  .tiptap h2,
  .tiptap h3 {
    margin: 0 0 8px;
  }
`;

function News() {
  const [newses, setNewses] = useState([]);
  const [formData, setFormData] = useState({
    date: "",
    translations: {
      kz: { title: "", description: "" },
      ru: { title: "", description: "" },
      en: { title: "", description: "" }
    },
    instagram: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [activeTab, setActiveTab] = useState("kz");
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewLang, setViewLang] = useState("kz");
  const newsPerPage = 9;
  const fileInputRef = useRef(null);
  const activeTabRef = useRef('kz');
  const { showNotification } = useNotification();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline
    ],
    content: '<p></p>',
    onUpdate: ({ editor: currentEditor }) => {
      const nextDescription = currentEditor.getHTML();
      setFormData(prev => ({
        ...prev,
        translations: {
          ...prev.translations,
          [activeTabRef.current]: {
            ...prev.translations[activeTabRef.current],
            description: nextDescription
          }
        }
      }));
    }
  })

  const toolbarState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      if (!currentEditor) {
        return {
          isBold: false,
          isItalic: false,
          isUnderline: false,
          canUndo: false,
          canRedo: false
        };
      }

      return {
        isBold: currentEditor.isActive('bold'),
        isItalic: currentEditor.isActive('italic'),
        isUnderline: currentEditor.isActive('underline'),
        canUndo: currentEditor.can().chain().focus().undo().run(),
        canRedo: currentEditor.can().chain().focus().redo().run()
      };
    }
  });

  useEffect(() => {
    activeTabRef.current = activeTab;
    if (!editor) return;

    const tabContent = formData.translations[activeTab].description || '<p></p>';
    if (editor.getHTML() !== tabContent) {
      editor.commands.setContent(tabContent, false);
    }
  }, [activeTab, editor, formData.translations]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name == 'instagram') {
      setFormData(prev => ({ ...prev, instagram: value }));
    } else if (name === "date") {
      setFormData(prev => ({ ...prev, date: value }));
    } else {
      setFormData(prev => ({
        ...prev,
        translations: {
          ...prev.translations,
          [activeTab]: {
            ...prev.translations[activeTab],
            [name]: value
          }
        }
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...newPreviews]);
    }
  };

  const removeNewFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const toggleExistingImage = (imgId) => {
    setImagesToRemove(prev =>
      prev.includes(imgId)
        ? prev.filter(id => id !== imgId)
        : [...prev, imgId]
    );
  };

  const handleEdit = (news) => {
    const translations = {
      kz: { title: "", description: "" },
      ru: { title: "", description: "" },
      en: { title: "", description: "" }
    };

    if (news.title?.translations) {
      news.title.translations.forEach(t => {
        if (translations[t.languageCode]) {
          translations[t.languageCode].title = t.textValue;
        }
      });
    }
    if (news.description?.translations) {
      news.description.translations.forEach(t => {
        if (translations[t.languageCode]) {
          translations[t.languageCode].description = t.textValue;
        }
      });
    }

    let formattedDate = "";
    if (news.date) {
      const dateObj = new Date(news.date);
      // Форматируем в YYYY-MM-DD
      formattedDate = dateObj.toISOString().split('T')[0];
    }
    setFormData({
      date: formattedDate,
      translations,
      instagram: news.instagram ?? '',
    });

    if (editor) {
      editor.commands.setContent(translations[activeTab].description || '<p></p>', false);
    }

    if (news.images) {
      setExistingImages(news.images);
    } else if (news.img_url) {
      // Fallback for old news structure
      setExistingImages([{ id: 'old', img_url: news.img_url }]);
    }

    setImagesToRemove([]);
    setSelectedFiles([]);
    setPreviewUrls([]);

    setEditId(news.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const submitFormData = new FormData();
      submitFormData.append('date', formData.date);
      submitFormData.append('instagram', formData.instagram);
      submitFormData.append('translations', JSON.stringify(formData.translations));

      if (selectedFiles.length > 0) {
        selectedFiles.forEach(file => {
          submitFormData.append('files', file);
        });
      }

      if (imagesToRemove.length > 0) {
        submitFormData.append('removeImages', JSON.stringify(imagesToRemove));
      }

      if (editId) {
        await patchNews({ id: editId, formData: submitFormData });
        showNotification("Новость успешно обновлена", "success");

        // Перезагружаем список новостей с сервера
        const response = await getNews();
        if (response?.news) {
          const sortedNews = response.news.sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          );
          setNewses(sortedNews);
        }
      } else {
        const response = await postNews(submitFormData);
        if (response?.news) {
          setNewses((prev) => [response.news, ...prev]);
        }
        showNotification("Новость успешно создана", "success");
      }

      // Очищаем форму
      setFormData({
        date: "",
        translations: {
          kz: { title: "", description: "" },
          ru: { title: "", description: "" },
          en: { title: "", description: "" }
        },
        instagram: '',
      });
      if (editor) {
        editor.commands.setContent('<p></p>', false);
      }
      setSelectedFiles([]);
      setPreviewUrls([]);
      setExistingImages([]);
      setImagesToRemove([]);
      setEditId(null);
      setErrors({});
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      showNotification("Ошибка при сохранении", "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Удалить эту новость?")) {
      try {
        await deleteNews(id);
        setNewses((prev) => {
          const updatedNews = prev.filter((n) => n.id !== id);
          const maxPage = Math.ceil(updatedNews.length / newsPerPage);
          if (currentPage > maxPage && currentPage > 1) {
            setCurrentPage(maxPage);
          }
          return updatedNews;
        });
        showNotification("Новость успешно удалена", "success");
      } catch (error) {
        showNotification("Ошибка при удалении", "error");
        console.log(error);
      }
    }
  };

  const cancelEdit = () => {
    setFormData({
      date: "",
      translations: {
        kz: { title: "", description: "" },
        ru: { title: "", description: "" },
        en: { title: "", description: "" }
      },
      instagram: ''
    });
    if (editor) {
      editor.commands.setContent('<p></p>', false);
    }
    setSelectedFiles([]);
    setPreviewUrls([]);
    setExistingImages([]);
    setImagesToRemove([]);
    setEditId(null);
    setErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    const loadNews = async () => {
      try {
        const response = await getNews();
        if (response?.news) {
          const sortedNews = response.news.sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          );
          setNewses(sortedNews);
        }
      } catch (error) {
        console.error("Error in loadNews:", error);
      }
    };
    loadNews();
  }, []);

  const indexOfLastNews = currentPage * newsPerPage;
  const indexOfFirstNews = indexOfLastNews - newsPerPage;
  const currentNews = newses.slice(indexOfFirstNews, indexOfLastNews);
  const totalPages = Math.ceil(newses.length / newsPerPage);

  const getPageNumbers = () => {
    const maxVisiblePages = window.innerWidth <= 480 ? 5 : 10;
    const pageNumbers = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    if (window.innerWidth <= 768) {
      const newsSection = document.querySelector('[data-news-section]');
      if (newsSection) {
        newsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const validateForm = () => {
    const validationErrors = {};

    const getPlainText = (value) =>
      value
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    if (!formData.date) {
      validationErrors.date = "Дата обязательна";
    }

    const langs = ['kz', 'ru', 'en'];
    const hasAtLeastOneLang = langs.some(lang =>
      formData.translations[lang].title.trim() &&
      getPlainText(formData.translations[lang].description)
    );

    if (!hasAtLeastOneLang) {
      validationErrors.general = "Заполните хотя бы один язык полностью";
    }

    return validationErrors;
  };

  const getTranslatedText = (textObj) => {
    if (!textObj?.translations) return "";

    const langTranslation = textObj.translations.find(t => t.languageCode === viewLang);
    if (langTranslation?.textValue) return langTranslation.textValue;

    const kzTranslation = textObj.translations.find(t => t.languageCode === 'kz');
    if (kzTranslation?.textValue) return kzTranslation.textValue;

    const ruTranslation = textObj.translations.find(t => t.languageCode === 'ru');
    if (ruTranslation?.textValue) return ruTranslation.textValue;

    const enTranslation = textObj.translations.find(t => t.languageCode === 'en');
    if (enTranslation?.textValue) return enTranslation.textValue;

    return "";
  };

  const getDescriptionPreview = (textObj) => {
    const html = getTranslatedText(textObj);
    if (!html) return '';

    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const handleToolbarMouseDown = (e) => {
    e.preventDefault();
  };

  return (
    <MainContainer>
      <Title>Управление новостями</Title>

      <FormContainer onSubmit={handleSubmit}>
        <FormGroup>
          <label>Дата</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            disabled={loading}
            onChange={handleChange}
            required
          />
          {errors.date && <ErrorMessage>{errors.date}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <label>Ссылка Instagram (необязательно)</label>
          <input
            type="text"
            name="instagram"
            value={formData.instagram}
            disabled={loading}
            onChange={handleChange}
            placeholder='Введите ссылка на Instagram пост (необязательно)'
          />
        </FormGroup>

        <FormGroup>
          <label>Изображения (можно выбрать несколько)</label>
          <input
            type="file"
            onChange={handleFileChange}
            accept="image/*"
            multiple
            ref={fileInputRef}
          />
          <ImagePreviewGrid>
            {/* Существующие изображения */}
            {existingImages.map((img) => (
              <PreviewWrapper key={img.id} isExisting toRemove={imagesToRemove.includes(img.id)}>
                <PreviewImage src={`${process.env.REACT_APP_API_URL}${img.img_url}`} alt="Existing" />
                {imagesToRemove.includes(img.id) ? (
                  <RestoreButton type="button" onClick={() => toggleExistingImage(img.id)}>Восстановить</RestoreButton>
                ) : (
                  <RemoveButton type="button" onClick={() => toggleExistingImage(img.id)}>×</RemoveButton>
                )}
              </PreviewWrapper>
            ))}

            {/* Новые изображения */}
            {previewUrls.map((url, index) => (
              <PreviewWrapper key={index}>
                <PreviewImage src={url} alt={`Preview ${index}`} />
                <RemoveButton type="button" onClick={() => removeNewFile(index)}>×</RemoveButton>
              </PreviewWrapper>
            ))}
          </ImagePreviewGrid>
        </FormGroup>

        <TabContainer>
          <TabButton
            type="button"
            active={activeTab === "kz"}
            onClick={() => setActiveTab("kz")}
          >
            🇰🇿 Қазақша
          </TabButton>
          <TabButton
            type="button"
            active={activeTab === "ru"}
            onClick={() => setActiveTab("ru")}
          >
            🇷🇺 Русский
          </TabButton>
          <TabButton
            type="button"
            active={activeTab === "en"}
            onClick={() => setActiveTab("en")}
          >
            🇬🇧 English
          </TabButton>
        </TabContainer>

        <FormGroup>
          <label>Заголовок ({activeTab.toUpperCase()})</label>
          <input
            type="text"
            name="title"
            value={formData.translations[activeTab].title}
            disabled={loading}
            onChange={handleChange}
            placeholder={`Введите заголовок на ${activeTab === 'kz' ? 'казахском' : activeTab === 'ru' ? 'русском' : 'английском'} `}
          />
        </FormGroup>

        <FormGroup>
          <label>Описание ({activeTab.toUpperCase()})</label>
          <EditorToolbar>
            <ToolbarButton
              type="button"
              data-variant="bold"
              $active={toolbarState.isBold}
              aria-pressed={toolbarState.isBold}
              onMouseDown={handleToolbarMouseDown}
              disabled={!editor || loading}
              onClick={() => editor.chain().focus().toggleBold().run()}
              title="Жирный"
            >
              B
            </ToolbarButton>
            <ToolbarButton
              type="button"
              data-variant="italic"
              $active={toolbarState.isItalic}
              aria-pressed={toolbarState.isItalic}
              onMouseDown={handleToolbarMouseDown}
              disabled={!editor || loading}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              title="Курсив"
            >
              I
            </ToolbarButton>
            <ToolbarButton
              type="button"
              data-variant="underline"
              $active={toolbarState.isUnderline}
              aria-pressed={toolbarState.isUnderline}
              onMouseDown={handleToolbarMouseDown}
              disabled={!editor || loading}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              title="Подчеркивание"
            >
              U
            </ToolbarButton>
            <ToolbarButton
              type="button"
              data-role="action"
              onMouseDown={handleToolbarMouseDown}
              disabled={!editor || loading || !toolbarState.canUndo}
              onClick={() => editor.chain().focus().undo().run()}
              title="Отменить"
            >
              Undo
            </ToolbarButton>
            <ToolbarButton
              type="button"
              data-role="action"
              onMouseDown={handleToolbarMouseDown}
              disabled={!editor || loading || !toolbarState.canRedo}
              onClick={() => editor.chain().focus().redo().run()}
              title="Повторить"
            >
              Redo
            </ToolbarButton>
          </EditorToolbar>
          <EditorWrapper>
            <EditorContent editor={editor} />
          </EditorWrapper>
        </FormGroup>

        {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}

        <ButtonGroup>
          <Button type="submit" disabled={loading}>
            {loading
              ? (editId ? "Обновление..." : "Создание...")
              : (editId ? "Обновить новость" : "Создать новость")
            }
          </Button>
          {editId && (
            <Button type="button" secondary onClick={cancelEdit} disabled={loading}>
              Отменить редактирование
            </Button>
          )}
        </ButtonGroup>
      </FormContainer>

      <NewsSection data-news-section>
        <SectionTitle>Список новостей</SectionTitle>

        <LanguageSwitcher>
          <LangButton active={viewLang === "kz"} onClick={() => setViewLang("kz")}>KZ</LangButton>
          <LangButton active={viewLang === "ru"} onClick={() => setViewLang("ru")}>RU</LangButton>
          <LangButton active={viewLang === "en"} onClick={() => setViewLang("en")}>EN</LangButton>
        </LanguageSwitcher>

        {window.innerWidth <= 480 && newses.length > newsPerPage && (
          <MobileScrollHint>
            Листайте страницы для просмотра всех новостей
          </MobileScrollHint>
        )}

        {newses.length > 0 ? (
          <>
            <NewsGrid>
              {currentNews.map((news) => (
                <NewsCard key={news.id}>
                  <NewsHeader>
                    <NewsTitle>{getTranslatedText(news.title)}</NewsTitle>
                    <NewsDate>
                      {new Date(news.date).toLocaleDateString("ru-RU")}
                    </NewsDate>
                  </NewsHeader>

                  {news.img_url && (
                    <NewsImage
                      src={`${process.env.REACT_APP_API_URL}${news.img_url} `}
                      alt={getTranslatedText(news.title)}
                    />
                  )}

                  <Description>{getDescriptionPreview(news.description)}</Description>

                  <ActionButtons>
                    <Button
                      type="button"
                      small
                      onClick={() => handleEdit(news)}
                      disabled={loading}
                    >
                      Редактировать
                    </Button>
                    <Button
                      type="button"
                      small
                      red
                      onClick={() => handleDelete(news.id)}
                      disabled={loading}
                    >
                      Удалить
                    </Button>
                  </ActionButtons>
                </NewsCard>
              ))}
            </NewsGrid>

            {totalPages > 1 && (
              <PaginationContainer>
                <PageButton
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  title="Предыдущая страница"
                >
                  &laquo;
                </PageButton>

                {pageNumbers.map((number) => (
                  <PageButton
                    key={number}
                    active={currentPage === number}
                    onClick={() => paginate(number)}
                    title={`Страница ${number} `}
                  >
                    {number}
                  </PageButton>
                ))}

                <PageButton
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                  title="Следующая страница"
                >
                  &raquo;
                </PageButton>
              </PaginationContainer>
            )}
          </>
        ) : (
          <EmptyState>
            Новостей пока нет. Создайте новую новость, используя форму выше.
          </EmptyState>
        )}
      </NewsSection>
    </MainContainer>
  );
}

export default News;